import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export type TreatmentPlanRow = Database['public']['Tables']['tooth_treatment_plans']['Row'];
export type ToothTreatmentRow = Database['public']['Tables']['tooth_treatments']['Row'];
export type SessionLockRow = Database['public']['Tables']['dental_session_locks']['Row'];

// ── Query Keys ────────────────────────────────────────────────────────────────

export const todaysSessionQueryKey = (patientId: string | null, clinicId: string | null) =>
  ['dental', 'session-today', patientId, clinicId] as const;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TodaysSessionData {
  plannedItems: TreatmentPlanRow[];
  completedToday: ToothTreatmentRow[];
  appointment: null | {
    id: string;
    starts_at: string;
    ends_at: string | null;
    notes: string | null;
    service_name: string | null;
    clinician_name: string | null;
  };
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Returns today's plan items, today's completed treatments, and today's appointment
 * for the given patient + clinic.
 */
export function useTodaysSession(patientId: string | null, clinicId: string | null) {
  return useQuery({
    queryKey: todaysSessionQueryKey(patientId, clinicId),
    enabled: Boolean(patientId && clinicId),
    queryFn: async (): Promise<TodaysSessionData> => {
      if (!patientId || !clinicId) {
        return { plannedItems: [], completedToday: [], appointment: null };
      }

      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

      const [planRes, treatRes, apptRes] = await Promise.all([
        supabase
          .from('tooth_treatment_plans')
          .select('*')
          .eq('patient_id', patientId)
          .eq('planned_date', today)
          .neq('plan_status', 'cancelled')
          .neq('plan_status', 'completed')
          .order('visit_number', { ascending: true }),

        supabase
          .from('tooth_treatments')
          .select('*')
          .eq('patient_id', patientId)
          .eq('treatment_date', today)
          .order('created_at', { ascending: true }),

        supabase
          .from('appointments')
          .select(`
            id,
            starts_at,
            ends_at,
            notes,
            services:service_id (name),
            users:clinician_id (raw_user_meta_data)
          `)
          .eq('patient_id', patientId)
          .eq('clinic_id', clinicId)
          .gte('starts_at', `${today}T00:00:00`)
          .lte('starts_at', `${today}T23:59:59`)
          .neq('status', 'cancelled')
          .order('starts_at', { ascending: true })
          .limit(1)
          .maybeSingle(),
      ]);

      if (planRes.error) throw planRes.error;
      if (treatRes.error) throw treatRes.error;
      // Appointment errors are non-fatal (appointments table may differ per deployment)

      const apptRaw = apptRes.data as Record<string, unknown> | null;
      let appointment: TodaysSessionData['appointment'] = null;
      if (apptRaw) {
        const svc = apptRaw['services'] as { name?: string } | null;
        const usr = apptRaw['users'] as {
          raw_user_meta_data?: { full_name?: string; name?: string };
        } | null;
        appointment = {
          id: String(apptRaw['id']),
          starts_at: String(apptRaw['starts_at']),
          ends_at: apptRaw['ends_at'] ? String(apptRaw['ends_at']) : null,
          notes: apptRaw['notes'] ? String(apptRaw['notes']) : null,
          service_name: svc?.name ?? null,
          clinician_name:
            usr?.raw_user_meta_data?.full_name ??
            usr?.raw_user_meta_data?.name ??
            null,
        };
      }

      return {
        plannedItems: (planRes.data ?? []) as TreatmentPlanRow[],
        completedToday: (treatRes.data ?? []) as ToothTreatmentRow[],
        appointment,
      };
    },
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

// ── Query Keys ────────────────────────────────────────────────────────────────

export const sessionLockQueryKey = (patientId: string | null, date: string) =>
  ['dental', 'session-lock', patientId, date] as const;

// ── Persisted session lock hooks (TAND-8: BEK 1225 § 14 compliance) ──────────

/**
 * Returns the current session lock row for the given patient + date.
 * Subscribes to Realtime updates via the query's refetch-on-window-focus.
 */
export function useSessionLockStatus(patientId: string | null, sessionDate?: string) {
  const date = sessionDate ?? new Date().toISOString().slice(0, 10);
  return useQuery<SessionLockRow | null>({
    queryKey: sessionLockQueryKey(patientId, date),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<SessionLockRow | null> => {
      if (!patientId) return null;
      const { data, error } = await supabase
        .from('dental_session_locks')
        .select('*')
        .eq('patient_id', patientId)
        .eq('session_date', date)
        .maybeSingle();
      if (error) throw error;
      return data as SessionLockRow | null;
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Locks the dental session for the given patient + date via the
 * `lock_dental_session` RPC (SECURITY DEFINER, clinic-member required).
 * BEK 1225 § 14: records clinician sign-off.
 */
export function useLockSession() {
  const queryClient = useQueryClient();

  return useMutation<
    SessionLockRow,
    Error,
    { patientId: string; sessionDate?: string; notes?: string }
  >({
    mutationFn: async ({ patientId, sessionDate, notes }) => {
      const date = sessionDate ?? new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase.rpc('lock_dental_session', {
        p_patient_id:   patientId,
        p_session_date: date,
        p_notes:        notes ?? undefined,
      });
      if (error) throw error;
      return data as SessionLockRow;
    },
    onSuccess: (data, variables) => {
      const date = variables.sessionDate ?? new Date().toISOString().slice(0, 10);
      queryClient.setQueryData(sessionLockQueryKey(variables.patientId, date), data);
    },
  });
}

/**
 * Unlocks a session (clinic_admin only) via `unlock_dental_session` RPC.
 */
export function useUnlockSession() {
  const queryClient = useQueryClient();

  return useMutation<
    boolean,
    Error,
    { lockId: string; patientId: string; sessionDate: string }
  >({
    mutationFn: async ({ lockId }) => {
      const { data, error } = await supabase.rpc('unlock_dental_session', {
        p_lock_id: lockId,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        sessionLockQueryKey(variables.patientId, variables.sessionDate),
        null,
      );
    },
  });
}

/**
 * @deprecated Use useSessionLockStatus + useLockSession instead.
 * Kept for components that haven't migrated yet.
 */
export function useSessionLock() {
  const [isLocked, setIsLocked] = useState(false);
  const lock = useCallback(() => setIsLocked(true), []);
  const unlock = useCallback(() => setIsLocked(false), []);
  return { isLocked, lock, unlock };
}
